import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, IconButton, Chip, CircularProgress } from '@mui/material';
import { useApp } from '../context/AppContext';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintableClientReport from '../components/PrintableClientReport';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { supabase } from '../lib/supabase';

const ClientManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const accountId = searchParams.get('accountId');
  const { 
    accounts,
    user 
  } = useApp();
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [hideZeroBalance, setHideZeroBalance] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [newEntry, setNewEntry] = useState({
    date: '',
    drNumber: '',
    amount: '',
    comments: ''
  });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [showEditAccountForm, setShowEditAccountForm] = useState(false);
  const [showEditEntryForm, setShowEditEntryForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editAccountData, setEditAccountData] = useState({
    name: '',
    department: '',
    address: '',
    contactNo: ''
  });
  const [showReceiptsDialog, setShowReceiptsDialog] = useState(false);
  const [selectedEntryReceipts, setSelectedEntryReceipts] = useState(null);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditReceiptForm, setShowEditReceiptForm] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Client Report', 14, 22);
    
    doc.setFontSize(12);
    doc.text(`Client: ${selectedAccount.name}`, 14, 32);
    doc.text(`Department: ${selectedAccount.department}`, 14, 38);
    doc.text(`Address: ${selectedAccount.address}`, 14, 44);
    doc.text(`Contact: ${selectedAccount.contactNo}`, 14, 50);
    doc.text(`Date Generated: ${new Date().toLocaleDateString()}`, 14, 56);

    const tableData = entries.map(entry => [
      entry.date,
      entry.drNumber,
      formatCurrency(entry.amount),
      formatCurrency(entry.collection),
      formatCurrency(entry.balance),
      entry.comments,
      entry.balance <= 0 ? 'Fully Paid' : 'Pending'
    ]);

    const totalAmount = entries.reduce((sum, entry) => sum + entry.amount, 0);
    const totalCollection = entries.reduce((sum, entry) => sum + entry.collection, 0);
    const totalBalance = entries.reduce((sum, entry) => sum + entry.balance, 0);

    tableData.push([
      'Total',
      '',
      formatCurrency(totalAmount),
      formatCurrency(totalCollection),
      formatCurrency(totalBalance),
      '',
      ''
    ]);

    doc.autoTable({
      startY: 65,
      head: [['Date', 'DR/SI No', 'Amount', 'Collection', 'Balance', 'Comments', 'Status']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [233, 192, 233] },
      styles: { fontSize: 8 }
    });

    doc.save(`${selectedAccount.name}_report.pdf`);
  };

  const fetchAccountData = async (id = selectedAccount?.id) => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('accounts')
        .select(`
          *,
          entries (
            *,
            collection_receipts (*)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setSelectedAccount(data);
    } catch (error) {
      console.error('Error fetching account data:', error);
      alert('Error loading account details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accountId) {
      const fetchInitialAccount = async () => {
        try {
          const { data, error } = await supabase
            .from('accounts')
            .select(`
              *,
              entries (
                *,
                collection_receipts (*)
              )
            `)
            .eq('id', accountId)
            .single();

          if (error) throw error;
          setSelectedAccount(data);
        } catch (error) {
          console.error('Error fetching initial account:', error);
          alert('Error loading account details');
        }
      };

      fetchInitialAccount();
    }
  }, [accountId]);

  useEffect(() => {
    if (selectedAccount) {
      fetchAccountData();
    }
  }, [selectedAccount?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedAccount) {
      setIsLoading(false);
    }
  }, [selectedAccount]);

  const handleAccountChange = (event, newValue) => {
    setSelectedAccount(newValue);
    if (newValue) {
      setSearchParams({ accountId: newValue.id });
      fetchAccountData(newValue.id);
    } else {
      setSearchParams({});
    }
  };

  const entries = selectedAccount?.entries || [];
  
  const filteredEntries = hideZeroBalance 
    ? entries.filter(entry => (entry.balance || 0) > 0) 
    : entries;

  const runningBalance = entries.reduce((sum, entry) => 
    sum + (entry.balance || 0), 0
  ) || 0;

  const runningDelivery = entries.reduce((sum, entry) => 
    sum + (entry.amount || 0), 0
  ) || 0;

  const runningCollection = entries.reduce((sum, entry) => 
    sum + (entry.collection || 0), 0
  ) || 0;

  const handleAddEntry = async () => {
    if (newEntry.date && newEntry.drNumber && newEntry.amount) {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('entries')
          .insert([{
            account_id: selectedAccount.id,
            date: newEntry.date,
            dr_number: newEntry.drNumber,
            amount: parseFloat(newEntry.amount),
            comments: newEntry.comments
          }])
          .select()
          .single();

        if (error) throw error;

        // Add to logs
        await supabase
          .from('logs')
          .insert([{
            type: 'Delivery',
            message: `${user.name} added delivery ${newEntry.drNumber} worth ${newEntry.amount} to ${selectedAccount.name} on ${newEntry.date}`,
            user_id: user.id
          }]);

        setNewEntry({ date: '', drNumber: '', amount: '', comments: '' });
        setShowEntryForm(false);
        fetchAccountData(); // Refresh data
      } catch (error) {
        alert('Error adding entry: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddPayment = async () => {
    if (paymentAmount && receiptNumber && selectedEntry) {
      try {
        setIsLoading(true);
        const amount = parseFloat(paymentAmount);

        // Add the collection receipt
        const { error: receiptError } = await supabase
          .from('collection_receipts')
          .insert([{
            entry_id: selectedEntry.id,
            receipt_number: receiptNumber,
            amount: amount,
            date: new Date().toISOString().split('T')[0]
          }]);

        if (receiptError) throw receiptError;

        // Update entry collection
        const { error: updateError } = await supabase
          .from('entries')
          .update({
            collection: selectedEntry.collection + amount
          })
          .eq('id', selectedEntry.id);

        if (updateError) throw updateError;

        // Add to logs
        await supabase
          .from('logs')
          .insert([{
            type: 'Payment',
            message: `${user.name} added payment of ${amount} to ${selectedAccount.name} ${selectedEntry.dr_number}`,
            user_id: user.id
          }]);

        setPaymentAmount('');
        setReceiptNumber('');
        setShowPaymentForm(false);
        setSelectedEntry(null);
        fetchAccountData(); // Refresh data
      } catch (error) {
        alert('Error adding payment: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const handleEditAccount = () => {
    if (selectedAccount) {
      setEditAccountData({
        name: selectedAccount.name,
        department: selectedAccount.department,
        address: selectedAccount.address,
        contactNo: selectedAccount.contactNo
      });
      setShowEditAccountForm(true);
    }
  };

  const handleUpdateAccount = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('accounts')
        .update({
          name: editAccountData.name,
          department: editAccountData.department,
          address: editAccountData.address,
          contact_no: editAccountData.contactNo
        })
        .eq('id', selectedAccount.id);

      if (error) throw error;

      // Add to logs
      await supabase
        .from('logs')
        .insert([{
          type: 'Info',
          message: `${user.name} updated account ${selectedAccount.name}`,
          user_id: user.id
        }]);

      setShowEditAccountForm(false);
      fetchAccountData();
    } catch (error) {
      alert('Error updating account: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('accounts')
        .delete()
        .eq('id', selectedAccount.id);

      if (error) throw error;

      // Add to logs
      await supabase
        .from('logs')
        .insert([{
          type: 'Info',
          message: `${user.name} deleted account ${selectedAccount.name}`,
          user_id: user.id
        }]);

      setSelectedAccount(null);
      setSearchParams({});
    } catch (error) {
      alert('Error deleting account: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry({
      ...entry,
      drNumber: entry.dr_number,
      collection: entry.collection || 0
    });
    setShowEditEntryForm(true);
  };

  const handleUpdateEntry = async () => {
    try {
      setIsLoading(true);
      
      // Update the entry in Supabase, excluding collection
      const { error: updateError } = await supabase
        .from('entries')
        .update({
          date: editingEntry.date,
          dr_number: editingEntry.drNumber,
          amount: parseFloat(editingEntry.amount),
          comments: editingEntry.comments
        })
        .eq('id', editingEntry.id);

      if (updateError) throw updateError;

      // Add to logs
      await supabase
        .from('logs')
        .insert([{
          type: 'Info',
          message: `${user.name} updated entry ${editingEntry.drNumber} in ${selectedAccount.name}`,
          user_id: user.id
        }]);

      // Refresh data and close dialog
      await fetchAccountData();
      setShowEditEntryForm(false);
      setEditingEntry(null);
    } catch (error) {
      alert('Error updating entry: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      setIsLoading(true);

      // First delete any associated collection receipts
      const { error: receiptsError } = await supabase
        .from('collection_receipts')
        .delete()
        .eq('entry_id', entryId);

      if (receiptsError) throw receiptsError;

      // Then delete the entry
      const { error: entryError } = await supabase
        .from('entries')
        .delete()
        .eq('id', entryId);

      if (entryError) throw entryError;

      // Add to logs
      await supabase
        .from('logs')
        .insert([{
          type: 'Info',
          message: `${user.name} deleted an entry from ${selectedAccount.name}`,
          user_id: user.id
        }]);

      // Refresh the data
      await fetchAccountData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteReceipt = async (receiptId) => {
    if (!window.confirm('Are you sure you want to delete this receipt?')) return;
    
    try {
      setIsLoading(true);
      
      // Get the receipt details first
      const { data: receipt, error: fetchError } = await supabase
        .from('collection_receipts')
        .select('*')
        .eq('id', receiptId)
        .single();

      if (fetchError) throw fetchError;

      // Delete the receipt
      const { error: deleteError } = await supabase
        .from('collection_receipts')
        .delete()
        .eq('id', receiptId);

      if (deleteError) throw deleteError;

      // Update the entry's collection
      const entry = selectedEntryReceipts;
      const newCollection = entry.collection - receipt.amount;

      const { error: updateError } = await supabase
        .from('entries')
        .update({ 
          collection: newCollection
        })
        .eq('id', receipt.entry_id);

      if (updateError) throw updateError;

      // Add to logs
      await supabase
        .from('logs')
        .insert([{
          type: 'Info',
          message: `${user.name} deleted receipt ${receipt.receipt_number} (${formatCurrency(receipt.amount)}) from ${selectedAccount.name}`,
          user_id: user.id
        }]);

      // Refresh the data and update the selected entry receipts
      await fetchAccountData();
      const updatedEntry = (await supabase
        .from('entries')
        .select('*, collection_receipts(*)')
        .eq('id', selectedEntryReceipts.id)
        .single()).data;
      setSelectedEntryReceipts(updatedEntry);
    } catch (error) {
      console.error('Error deleting receipt:', error);
      alert('Error deleting receipt: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditReceipt = async () => {
    try {
      setIsLoading(true);
      const oldAmount = editingReceipt.amount;
      const newAmount = parseFloat(editingReceipt.newAmount);
      const entry = selectedEntryReceipts;

      // Update the receipt
      const { error: updateReceiptError } = await supabase
        .from('collection_receipts')
        .update({
          receipt_number: editingReceipt.receipt_number,
          amount: newAmount,
          date: editingReceipt.date
        })
        .eq('id', editingReceipt.id);

      if (updateReceiptError) throw updateReceiptError;

      // Update the entry's collection
      const newCollection = entry.collection - oldAmount + newAmount;
      const { error: updateEntryError } = await supabase
        .from('entries')
        .update({ 
          collection: newCollection
        })
        .eq('id', entry.id);

      if (updateEntryError) throw updateEntryError;

      // Add to logs
      await supabase
        .from('logs')
        .insert([{
          type: 'Info',
          message: `${user.name} updated receipt ${editingReceipt.receipt_number} from ${formatCurrency(oldAmount)} to ${formatCurrency(newAmount)} in ${selectedAccount.name}`,
          user_id: user.id
        }]);

      // Refresh both the main data and the receipts dialog data
      await fetchAccountData();
      const updatedEntry = (await supabase
        .from('entries')
        .select('*, collection_receipts(*)')
        .eq('id', selectedEntryReceipts.id)
        .single()).data;
      setSelectedEntryReceipts(updatedEntry);

      setShowEditReceiptForm(false);
      setEditingReceipt(null);
    } catch (error) {
      console.error('Error updating receipt:', error);
      alert('Error updating receipt: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button 
          onClick={() => {
            setError(null);
            fetchAccountData();
          }}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>Client Management</Typography>
      
      {/* Search Account Autocomplete - Full Width */}
      <Box sx={{ mb: 4, width: '100%' }}>
        <Autocomplete
          value={selectedAccount}
          onChange={handleAccountChange}
          options={accounts || []}
          getOptionLabel={(option) => option.name || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Account"
              variant="outlined"
              placeholder="Type to search account..."
              fullWidth
              sx={{
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1
                }
              }}
            />
          )}
        />
      </Box>

      {selectedAccount && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            backgroundColor: '#f8f9fa',
            p: 3,
            borderRadius: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 800,
                  mb: 1 
                }}
              >
                {selectedAccount.name}
              </Typography>
              <Typography 
                sx={{ 
                  fontWeight: 700,
                  fontSize: '1.1rem'
                }}
              >
                Running Balance: {formatCurrency(runningBalance)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handlePrint}
                sx={{ 
                  backgroundColor: '#E3F2FD',
                  color: '#2196F3',
                  padding: '8px',
                  '&:hover': { 
                    backgroundColor: '#BBDEFB',
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s'
                  }
                }}
              >
                <PrintIcon sx={{ fontSize: '1.5rem' }} />
              </IconButton>
              <IconButton
                onClick={handleExportPDF}
                sx={{ 
                  backgroundColor: '#FFF3E0',
                  color: '#FF9800',
                  padding: '8px',
                  '&:hover': { 
                    backgroundColor: '#FFE0B2',
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s'
                  }
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: '1.5rem' }} />
              </IconButton>
              {user.role === 'admin' && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={handleEditAccount}
                    sx={{ 
                      backgroundColor: '#E8F5E9',
                      color: '#4CAF50',
                      padding: '8px',
                      '&:hover': { 
                        backgroundColor: '#C8E6C9',
                        transform: 'scale(1.1)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: '1.5rem' }} />
                  </IconButton>
                  <IconButton
                    onClick={handleDeleteAccount}
                    sx={{ 
                      backgroundColor: '#FFEBEE',
                      color: '#F44336',
                      padding: '8px',
                      '&:hover': { 
                        backgroundColor: '#FFCDD2',
                        transform: 'scale(1.1)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '1.5rem' }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained"
                onClick={() => setShowEntryForm(true)}
                sx={{ 
                  backgroundColor: '#e9c0e9',
                  color: 'black',
                  '&:hover': { backgroundColor: '#d4abd4' }
                }}
              >
                Add Entry
              </Button>
              <Button 
                variant="outlined"
                onClick={() => setHideZeroBalance(!hideZeroBalance)}
                sx={{ color: 'black', borderColor: 'black' }}
              >
                {hideZeroBalance ? 'Show All Entries' : 'Hide Entries with zero balance'}
              </Button>
            </Box>
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>DR/SI No</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Collection</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Collection Receipts</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Comments</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries?.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell>{entry.dr_number}</TableCell>
                  <TableCell align="right">{formatCurrency(entry.amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(entry.collection)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedEntryReceipts(entry);
                          setShowReceiptsDialog(true);
                        }}
                        sx={{ 
                          backgroundColor: '#E1BEE7',
                          color: '#9C27B0',
                          padding: '6px',
                          '&:hover': { 
                            backgroundColor: '#CE93D8',
                            transform: 'scale(1.1)',
                            transition: 'transform 0.2s'
                          }
                        }}
                      >
                        <ReceiptIcon fontSize="small" />
                      </IconButton>
                      {entry.collection_receipts?.length > 0 && (
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          ({entry.collection_receipts.length})
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(entry.balance)}</TableCell>
                  <TableCell>{entry.comments}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                      {entry.balance <= 0 ? (
                        <Chip 
                          label="Fully Paid" 
                          sx={{ 
                            backgroundColor: '#90EE90',
                            color: 'black',
                            height: '32px',
                            minWidth: '100px'
                          }} 
                        />
                      ) : (
                        <Chip
                          label="Add Payment"
                          onClick={() => {
                            setSelectedEntry(entry);
                            setShowPaymentForm(true);
                          }}
                          sx={{ 
                            backgroundColor: '#e9c0e9',
                            color: 'black',
                            height: '32px',
                            minWidth: '100px',
                            cursor: 'pointer',
                            '&:hover': { 
                              backgroundColor: '#d4abd4'
                            }
                          }}
                        />
                      )}
                      {user.role === 'admin' && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEditEntry(entry)}
                            sx={{ 
                              backgroundColor: '#E8F5E9',
                              color: '#4CAF50',
                              padding: '6px',
                              '&:hover': { 
                                backgroundColor: '#C8E6C9',
                                transform: 'scale(1.1)',
                                transition: 'transform 0.2s'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteEntry(entry.id)}
                            disabled={isLoading}
                            sx={{ 
                              backgroundColor: '#FFEBEE',
                              color: '#F44336',
                              padding: '6px',
                              '&:hover': { 
                                backgroundColor: '#FFCDD2',
                                transform: 'scale(1.1)',
                                transition: 'transform 0.2s'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Add Entry Dialog */}
      <Dialog open={showEntryForm} onClose={() => setShowEntryForm(false)}>
        <DialogTitle>Add New Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField 
              label="Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newEntry.date}
              onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
            />
            <TextField 
              label="DR/SI No"
              variant="outlined"
              fullWidth
              value={newEntry.drNumber}
              onChange={(e) => setNewEntry(prev => ({ ...prev, drNumber: e.target.value }))}
            />
            <TextField 
              label="Amount"
              type="number"
              variant="outlined"
              fullWidth
              value={newEntry.amount}
              onChange={(e) => setNewEntry(prev => ({ ...prev, amount: e.target.value }))}
            />
            <TextField 
              label="Comments"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={newEntry.comments}
              onChange={(e) => setNewEntry(prev => ({ ...prev, comments: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEntryForm(false)}>Cancel</Button>
          <Button 
            onClick={handleAddEntry}
            sx={{ 
              backgroundColor: '#e9c0e9',
              color: 'black',
              '&:hover': { backgroundColor: '#d4abd4' }
            }}
          >
            Add Entry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={showPaymentForm} onClose={() => setShowPaymentForm(false)}>
        <DialogTitle>Add Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Typography>
              DR/SI No: {selectedEntry?.drNumber}
            </Typography>
            <Typography>
              Outstanding Balance: {selectedEntry ? formatCurrency(selectedEntry.balance) : '-'}
            </Typography>
            <TextField 
              label="Receipt Number"
              variant="outlined"
              fullWidth
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
              required
            />
            <TextField 
              label="Payment Amount"
              type="number"
              variant="outlined"
              fullWidth
              value={paymentAmount}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (!isNaN(value) && value <= selectedEntry.balance) {
                  setPaymentAmount(e.target.value);
                }
              }}
              error={parseFloat(paymentAmount) > selectedEntry?.balance}
              helperText={parseFloat(paymentAmount) > selectedEntry?.balance ? 
                "Payment cannot exceed remaining balance" : ""}
              InputProps={{
                inputProps: { 
                  max: selectedEntry?.balance,
                  min: 0
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentForm(false)}>Cancel</Button>
          <Button onClick={handleAddPayment}>Add Payment</Button>
        </DialogActions>
      </Dialog>

      {/* Add Edit Account Dialog */}
      <Dialog open={showEditAccountForm} onClose={() => setShowEditAccountForm(false)}>
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField 
              label="Account Name"
              variant="outlined"
              fullWidth
              value={editAccountData.name}
              onChange={(e) => setEditAccountData(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField 
              label="Department"
              variant="outlined"
              fullWidth
              value={editAccountData.department}
              onChange={(e) => setEditAccountData(prev => ({ ...prev, department: e.target.value }))}
            />
            <TextField 
              label="Address"
              variant="outlined"
              fullWidth
              value={editAccountData.address}
              onChange={(e) => setEditAccountData(prev => ({ ...prev, address: e.target.value }))}
            />
            <TextField 
              label="Contact No."
              variant="outlined"
              fullWidth
              value={editAccountData.contactNo}
              onChange={(e) => setEditAccountData(prev => ({ ...prev, contactNo: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditAccountForm(false)}>Cancel</Button>
          <Button onClick={handleUpdateAccount}>Update Account</Button>
        </DialogActions>
      </Dialog>

      {/* Add Edit Entry Dialog */}
      <Dialog open={showEditEntryForm} onClose={() => setShowEditEntryForm(false)}>
        <DialogTitle>Edit Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField 
              label="Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editingEntry?.date || ''}
              onChange={(e) => setEditingEntry(prev => ({ ...prev, date: e.target.value }))}
            />
            <TextField 
              label="DR/SI No"
              variant="outlined"
              fullWidth
              value={editingEntry?.drNumber || ''}
              onChange={(e) => setEditingEntry(prev => ({ ...prev, drNumber: e.target.value }))}
            />
            <TextField 
              label="Amount"
              type="number"
              variant="outlined"
              fullWidth
              value={editingEntry?.amount || ''}
              onChange={(e) => {
                const amount = parseFloat(e.target.value);
                setEditingEntry(prev => ({
                  ...prev,
                  amount,
                  balance: amount - (prev?.collection || 0)
                }));
              }}
            />
            <TextField 
              label="Collection"
              type="number"
              variant="outlined"
              fullWidth
              value={editingEntry?.collection || ''}
              disabled
              helperText="Collection can only be modified through receipts"
            />
            <TextField 
              label="Balance"
              type="number"
              variant="outlined"
              fullWidth
              value={editingEntry?.balance || ''}
              disabled
            />
            <TextField 
              label="Comments"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={editingEntry?.comments || ''}
              onChange={(e) => setEditingEntry(prev => ({ ...prev, comments: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditEntryForm(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateEntry}
            sx={{ 
              backgroundColor: '#e9c0e9',
              color: 'black',
              '&:hover': { backgroundColor: '#d4abd4' }
            }}
          >
            Update Entry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Receipts Dialog */}
      <Dialog 
        open={showReceiptsDialog} 
        onClose={() => setShowReceiptsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Collection Receipts</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              DR/SI No: {selectedEntryReceipts?.drNumber}
            </Typography>
            <Typography variant="subtitle1">
              Amount: {formatCurrency(selectedEntryReceipts?.amount)}
            </Typography>
            <Typography variant="subtitle1">
              Total Collection: {formatCurrency(selectedEntryReceipts?.collection)}
            </Typography>
            <Typography variant="subtitle1">
              Balance: {formatCurrency(selectedEntryReceipts?.balance)}
            </Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Receipt Number</TableCell>
                <TableCell align="right">Amount</TableCell>
                {user.role === 'admin' && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedEntryReceipts?.collection_receipts?.map((receipt) => (
                <TableRow key={receipt.id}>
                  <TableCell>{receipt.date}</TableCell>
                  <TableCell>{receipt.receipt_number}</TableCell>
                  <TableCell align="right">{formatCurrency(receipt.amount)}</TableCell>
                  {user.role === 'admin' && (
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingReceipt({
                              ...receipt,
                              newAmount: receipt.amount
                            });
                            setShowEditReceiptForm(true);
                          }}
                          sx={{ 
                            backgroundColor: '#e9c0e9',
                            '&:hover': { backgroundColor: '#d4abd4' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteReceipt(receipt.id)}
                          sx={{ 
                            backgroundColor: '#ff9999',
                            '&:hover': { backgroundColor: '#ff6666' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReceiptsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Edit Receipt Dialog */}
      <Dialog 
        open={showEditReceiptForm} 
        onClose={() => setShowEditReceiptForm(false)}
      >
        <DialogTitle>Edit Receipt</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField 
              label="Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editingReceipt?.date || ''}
              onChange={(e) => setEditingReceipt(prev => ({ 
                ...prev, 
                date: e.target.value 
              }))}
            />
            <TextField 
              label="Receipt Number"
              variant="outlined"
              fullWidth
              value={editingReceipt?.receipt_number || ''}
              onChange={(e) => setEditingReceipt(prev => ({ 
                ...prev, 
                receipt_number: e.target.value 
              }))}
            />
            <TextField 
              label="Amount"
              type="number"
              variant="outlined"
              fullWidth
              value={editingReceipt?.newAmount || ''}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                const entry = selectedAccount.entries.find(e => e.id === editingReceipt.entry_id);
                const maxAmount = entry.amount - (entry.collection - editingReceipt.amount);
                
                if (!isNaN(value) && value <= maxAmount) {
                  setEditingReceipt(prev => ({
                    ...prev,
                    newAmount: value
                  }));
                }
              }}
              helperText="Amount cannot exceed the entry's remaining balance"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowEditReceiptForm(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditReceipt}
            disabled={isLoading}
            sx={{ 
              backgroundColor: '#e9c0e9',
              color: 'black',
              '&:hover': { backgroundColor: '#d4abd4' }
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Updating...
              </Box>
            ) : (
              'Update Receipt'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <div style={{ display: 'none' }}>
        <PrintableClientReport
          ref={componentRef}
          account={selectedAccount}
          entries={entries}
          formatCurrency={formatCurrency}
        />
      </div>
    </Box>
  );
};

export default ClientManagement; 